import { useState } from 'react';

import type Enums from '@core/types/api/Enums';
import type { NestedKeyOf } from '@core/utils/types';

import type { FilterByOpts, NamedFilterOpts, SearchFilterOpts } from '../../utils/ApiClient';
import type { Column } from './TableDataModel';
import { getAndUpgradeV4PreservedState } from './utils/PreservedStateV4';

// All properties in this type should always be optional, as a reminder that we
// need appropriate error handling for cases where the client's data storage
// doesn't give the results we expect.
export type DataTablePreservedState<TRow extends { id: string }> = {
  name?: string;
  sort?: { name: string; direction: Enums.SortDirection };
  searchFilter?: SearchFilterOpts;
  checkboxFilters?: NamedFilterOpts<TRow>[];
  yearFilter?: NamedFilterOpts<TRow>;
  marketFilter?: NamedFilterOpts<TRow>;
  locationFilter?: NamedFilterOpts<TRow>;
  divisionFilter?: NamedFilterOpts<TRow>;
  columnFilters?: FilterByOpts<TRow>[];
  activeTabFilterValues?: FilterByOpts<TRow>['values'];
  renderedColumns?: Column<TRow>[];
  columnOrder?: NestedKeyOf<TRow>[];
};

export const getStateKey = (tableName: string): string => `DataTablePreservedState:v5:${tableName}`;

const getPreservedState = <TRow extends { id: string }>(
  tableName: string,
): DataTablePreservedState<TRow> => {
  const str = localStorage.getItem(getStateKey(tableName));
  if (str === null) {
    // If there are no saved settings for the specified table, return an empty state.
    return {};
  }

  try {
    const value = JSON.parse(str);
    if (value === null || Array.isArray(value) || typeof value !== 'object') {
      throw new Error();
    }
    return value;
  } catch {
    // If the stored settings can't be parsed as a JSON object, delete the data and ignore it.
    localStorage.removeItem(getStateKey(tableName));
    return {};
  }
};

/**
 * useTablePreservedState is a hook that loads any previously-saved config for
 * the specified DataTable from localStorage.  It returns the loaded data,
 * together with a callback to save that table's new configurations and a
 * callback to delete that table's previously-saved configuration.
 *
 * If tableName is undefined, state is silently discarded (neither retrieved nor
 * persisted).
 *
 * @param tableName The name of the DataTable whose state is managed by this hook.
 * @returns The search+filtering state of the named DataTable.
 */
export const useTablePreservedState = <TRow extends { id: string }>(
  tableName: string | undefined,
): [DataTablePreservedState<TRow>, (state: DataTablePreservedState<TRow>) => void, () => void] => {
  const [preservedState, setPreservedState] = useState((): DataTablePreservedState<TRow> => {
    if (tableName === undefined) {
      return {};
    }

    let state: DataTablePreservedState<TRow> = getPreservedState(tableName);
    if (Object.keys(state).length === 0) {
      state = getAndUpgradeV4PreservedState(tableName);
    }
    return state;
  });

  const preserveState = (data: DataTablePreservedState<TRow>): void => {
    if (tableName === undefined) {
      return;
    }

    localStorage.setItem(getStateKey(tableName), JSON.stringify(data));
    setPreservedState(data);
  };

  const deleteState = (): void => {
    if (tableName === undefined) {
      return;
    }

    localStorage.removeItem(getStateKey(tableName));
    setPreservedState({});
  };

  return [preservedState, preserveState, deleteState];
};
